import React, { useState } from "react";
import { api, formatError } from "@/lib/api";
import useAsyncLoad from "@/hooks/useAsyncLoad";
import { Loading, LoadFailed } from "@/components/LoadState";
import { toast } from "@/lib/toast";
import {
  FileCheck, CheckCircle2, Clock, AlertTriangle, RefreshCw, Eye, Loader2, ShieldCheck, X,
} from "lucide-react";

const FILTERS = [
  ["pending_review", "Pending"],
  ["verified", "Verified"],
  ["expired", "Expired"],
  ["rejected", "Rejected"],
  ["all", "All"],
];

const STATUS_META = {
  not_submitted: { label: "Not submitted", cls: "border-sniper text-muted-foreground" },
  pending_review: { label: "Pending", cls: "border-[#7A5A12] text-[#E8C46A]" },
  verified: { label: "Verified", cls: "border-[#1F6BA8] text-[#4a9fd6]" },
  expired: { label: "Expired", cls: "border-[#C2600F] text-[#E8A05A]" },
  rejected: { label: "Rejected", cls: "border-[#C2600F] text-[#E8A05A]" },
};

function Chip({ status }) {
  const m = STATUS_META[status] || STATUS_META.not_submitted;
  return <span className={`inline-flex items-center font-mono text-[10px] uppercase tracking-wide px-2 py-0.5 border ${m.cls}`}>{m.label}</span>;
}

function DetailPanel({ licenseId, onChanged }) {
  const { status, data, error, retry } = useAsyncLoad(
    (signal) => api.get(`/admin/licenses/${licenseId}`, { signal }).then((r) => r.data),
    [licenseId]
  );
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [docBusy, setDocBusy] = useState(false);

  const viewDoc = async () => {
    setDocBusy(true);
    try {
      const res = await api.get(`/admin/licenses/${licenseId}/document`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Couldn't open the document.");
    } finally { setDocBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/licenses/${licenseId}/verify`);
      toast.success("FFL verified.");
      retry(); onChanged();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    if (reason.trim().length < 3) { toast.error("Add a short reason for the dealer."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("reason", reason.trim());
      await api.post(`/admin/licenses/${licenseId}/reject`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("FFL sent back to the dealer.");
      setRejecting(false); setReason("");
      retry(); onChanged();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (status === "loading") return <Loading label="Loading record…" testid="license-detail-loading" />;
  if (status === "error") return <LoadFailed reason={error} onRetry={retry} testid="license-detail-load-failed" />;

  const rec = data;
  const rows = [
    ["FFL number (typed)", rec.ffl_number],
    ["Type", rec.ffl_type],
    ["Expiration", rec.expiration_date],
    ["Licensee (typed)", rec.licensee_name],
    ["Premises (typed)", rec.premises_address],
  ];

  return (
    <div className="border border-sniper bg-surface p-5" data-testid="license-detail">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="font-heading text-lg uppercase tracking-tight flex items-center gap-2"><FileCheck className="w-4 h-4 text-olive-bright" /> FFL review</h2>
        <Chip status={rec.status} />
      </div>
      <p className="font-mono text-[11px] text-muted-foreground mt-1">Eyeball the typed values against the document (manual ATF eZ Check — we never scrape ATF).</p>

      <div className="mt-4 grid sm:grid-cols-2 gap-2 font-mono text-[12px]">
        {rows.map(([k, v]) => (
          <div key={k} className="min-w-0">
            <div className="text-[10px] uppercase text-muted-foreground">{k}</div>
            <div className="text-foreground break-words">{v || "—"}</div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {rec.has_document ? (
          <button onClick={viewDoc} disabled={docBusy} data-testid="license-view-doc"
            className="inline-flex items-center gap-2 border border-sniper hover:border-olive text-foreground font-mono uppercase text-xs px-3 py-2 transition-colors disabled:opacity-50">
            {docBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} View document
          </button>
        ) : <span className="font-mono text-[11px] text-muted-foreground">No document on file.</span>}
        <span className="font-mono text-[10px] text-muted-foreground">Opening logs an audit event.</span>
      </div>

      {(rec.status === "pending_review" || rec.status === "expired" || rec.status === "rejected") && (
        <div className="mt-5 pt-4 border-t border-sniper">
          {!rejecting ? (
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={verify} disabled={busy} data-testid="license-verify-btn"
                className="inline-flex items-center gap-2 bg-[#57682A] hover:bg-[#748A3D] disabled:opacity-50 text-white font-heading font-bold uppercase text-xs px-4 py-2.5 transition-colors">
                <CheckCircle2 className="w-4 h-4" /> {busy ? "Verifying…" : "Verify FFL"}
              </button>
              <button onClick={() => setRejecting(true)} disabled={busy} data-testid="license-reject-open"
                className="inline-flex items-center gap-2 border border-[#C2600F]/60 text-[#E8A05A] hover:bg-[#C2600F]/10 font-heading font-bold uppercase text-xs px-4 py-2.5 transition-colors">
                <X className="w-4 h-4" /> Send back
              </button>
            </div>
          ) : (
            <div data-testid="license-reject-form">
              <label className="font-mono text-[10px] uppercase text-secondary-foreground block mb-1">Reason (shown to the dealer)</label>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} data-testid="license-reject-reason"
                className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-mono text-sm" placeholder="e.g. The document is blurry — please upload a clearer scan." />
              <div className="flex items-center gap-2 mt-2">
                <button onClick={reject} disabled={busy} data-testid="license-reject-confirm"
                  className="inline-flex items-center gap-2 bg-[#C2600F] hover:bg-[#a85210] disabled:opacity-50 text-white font-heading font-bold uppercase text-xs px-4 py-2 transition-colors">
                  {busy ? "Sending…" : "Confirm"}
                </button>
                <button onClick={() => { setRejecting(false); setReason(""); }} className="font-mono uppercase text-[10px] text-muted-foreground hover:text-foreground px-2 py-2">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {(rec.history || []).length > 0 && (
        <div className="mt-5 pt-4 border-t border-sniper">
          <div className="font-mono text-[10px] uppercase text-muted-foreground mb-2">History</div>
          <div className="space-y-1.5">
            {rec.history.slice().reverse().map((h, i) => (
              <div key={i} className="font-mono text-[11px] text-secondary-foreground flex items-start gap-2">
                <span className="text-muted-foreground shrink-0">{h.at ? new Date(h.at).toLocaleDateString() : ""}</span>
                <span className="text-foreground">{h.action}</span>
                <span className="text-muted-foreground">by {h.actor_username || "—"}</span>
                {h.reason ? <span className="text-[#E8A05A]">· {h.reason}</span> : null}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminLicenses() {
  const [filter, setFilter] = useState("pending_review");
  const [selected, setSelected] = useState(null);
  const { status, data, error, retry } = useAsyncLoad(
    (signal) => api.get(`/admin/licenses${filter === "all" ? "" : `?status=${filter}`}`, { signal }).then((r) => r.data),
    [filter]
  );
  const rows = data?.licenses || [];

  return (
    <div data-testid="admin-licenses">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-5 h-5 text-olive-bright" />
        <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">Licenses &amp; Compliance</h1>
      </div>
      <p className="font-mono text-[11px] text-muted-foreground mb-4">Verify dealer FFL records against their private documents.</p>

      <div className="flex items-center gap-1 mb-4 flex-wrap" data-testid="license-filters">
        {FILTERS.map(([v, l]) => (
          <button key={v} onClick={() => { setFilter(v); setSelected(null); }} data-testid={`license-filter-${v}`}
            className={`font-mono uppercase text-[11px] tracking-wide px-3 py-1.5 border transition-colors ${filter === v ? "border-olive-bright bg-olive/20 text-foreground" : "border-sniper text-secondary-foreground hover:text-foreground"}`}>
            {l}
          </button>
        ))}
        <button onClick={retry} title="Refresh" className="ml-auto inline-flex items-center gap-1.5 border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-[11px] px-3 py-1.5 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {status === "loading" && <Loading label="Loading queue…" testid="licenses-queue-loading" />}
      {status === "error" && <LoadFailed reason={error} onRetry={retry} testid="licenses-queue-load-failed" />}
      {status === "ok" && (
        <div className="grid lg:grid-cols-[1fr_420px] gap-4 items-start">
          <div className="border border-sniper bg-surface overflow-hidden" data-testid="license-queue">
            {rows.length === 0 ? (
              <p className="p-6 font-mono text-xs uppercase text-muted-foreground" data-testid="license-queue-empty">No licenses in this view.</p>
            ) : (
              <div className="divide-y divide-sniper">
                {rows.map((r) => (
                  <button key={r.license_id} onClick={() => setSelected(r.license_id)} data-testid={`license-row-${r.license_id}`}
                    className={`w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors ${selected === r.license_id ? "bg-olive/10" : ""}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-body text-sm text-foreground truncate">{r.licensee_name || "—"}</div>
                      <div className="font-mono text-[11px] text-muted-foreground truncate">FFL {r.ffl_type} · {r.ffl_number} · exp {r.expiration_date}</div>
                    </div>
                    <Chip status={r.status} />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            {selected ? <DetailPanel licenseId={selected} onChanged={retry} />
              : <div className="border border-dashed border-sniper p-8 text-center font-mono text-xs uppercase text-muted-foreground" data-testid="license-detail-empty">Select a record to review</div>}
          </div>
        </div>
      )}
    </div>
  );
}
